
import { supabase } from './supabase/client';
import { format } from 'date-fns';
import { BookingType, BookingEmailInfo, ClassEmailInfo } from './supabase/types';

interface SendBookingConfirmationOptions {
  booking: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    groupSize?: number;
  };
  classDetails: {
    title: string;
    day: string;
    date: string;
    time: string;
    duration: string;
  };
  baseUrl: string;
  language?: string;
}

export const sendBookingConfirmation = async ({
  booking,
  classDetails,
  baseUrl,
  language = 'en'
}: SendBookingConfirmationOptions): Promise<{ success: boolean; error?: string }> => {
  try {
    // Format the class date and time for display in the email
    const formattedDate = classDetails.date ? format(new Date(classDetails.date), 'MMMM d, yyyy') : classDetails.day;
    
    // Create all the needed data for the API call
    const emailData = {
      booking: {
        id: booking.id,
        firstName: booking.firstName,
        lastName: booking.lastName,
        email: booking.email,
        groupSize: booking.groupSize
      },
      classDetails: {
        title: classDetails.title || 'Pottery Class',
        day: classDetails.day || '',
        date: classDetails.date || '',
        time: classDetails.time || '',
        duration: classDetails.duration || '1.5 hours',
        formattedDate
      },
      baseUrl,
      language
    };
    
    console.log("Sending booking confirmation with data:", emailData);
    
    // Get the Supabase URL and anon key from environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error("Missing Supabase credentials for email sending");
      throw new Error("Application configuration error");
    }
    
    // Call the dedicated Supabase Edge Function for booking confirmations
    const response = await fetch(
      `${supabaseUrl}/functions/v1/send-booking-confirmation`, 
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          type: 'booking-confirmation',
          emailData
        })
      }
    );
    
    if (!response.ok) {
      let errorMessage = "Unknown error";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `HTTP error ${response.status}`;
        console.error('Error from booking confirmation endpoint:', errorData);
      } catch (e) {
        console.error('Could not parse error response:', e);
      }
      throw new Error(`Failed to send email: ${errorMessage}`);
    }
    
    const data = await response.json();
    console.log('Email confirmation response:', data);
    return { success: true };
  } catch (error: any) {
    console.error('Error sending booking confirmation email:', error);
    return { success: false, error: error.message || 'Failed to send email' };
  }
};

/**
 * Sends a booking reschedule confirmation email
 */
export const sendRescheduleConfirmation = async (
  booking: BookingEmailInfo,
  originalClass: ClassEmailInfo,
  newClass: ClassEmailInfo
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Format the class dates for display in the email
    const originalFormattedDate = originalClass.date 
      ? format(new Date(originalClass.date), 'MMMM d, yyyy') 
      : originalClass.day;
    
    const newFormattedDate = newClass.date 
      ? format(new Date(newClass.date), 'MMMM d, yyyy') 
      : newClass.day;
    
    // Get base URL from environment
    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    
    // Create all the needed data for the API call
    const emailData = {
      booking,
      originalClass: {
        ...originalClass,
        formattedDate: originalFormattedDate
      },
      newClass: {
        ...newClass,
        formattedDate: newFormattedDate
      },
      baseUrl
    };
    
    console.log("Sending reschedule confirmation with data:", emailData);
    
    // Get the Supabase URL and anon key from environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error("Missing Supabase credentials for email sending");
      throw new Error("Application configuration error");
    }
    
    // Call the dedicated Supabase Edge Function for booking rescheduling confirmations
    const response = await fetch(
      `${supabaseUrl}/functions/v1/send-booking-confirmation`, 
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          type: 'reschedule-confirmation',
          emailData
        })
      }
    );
    
    if (!response.ok) {
      let errorMessage = "Unknown error";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `HTTP error ${response.status}`;
        console.error('Error from reschedule confirmation endpoint:', errorData);
      } catch (e) {
        console.error('Could not parse error response:', e);
      }
      throw new Error(`Failed to send email: ${errorMessage}`);
    }
    
    const data = await response.json();
    console.log('Email confirmation response:', data);
    return { success: true };
  } catch (error: any) {
    console.error('Error sending reschedule confirmation email:', error);
    return { success: false, error: error.message || 'Failed to send email' };
  }
};
